import {
  getTokenCatalog,
  getTokenLegRole,
  NATIVE_TOKEN_ADDRESS,
  orderPairLegsForDisplay,
  type TokenLegRole,
} from '@sirius-cybernetics-42/product-config'

import { isNativeLegAddress, toOnChainAddress } from '@/data/marketApi/normalizeMarketToken'
import { isNativeCurrencySymbol, nativeCurrencyId } from '@/config/native'
import { isAddress } from '@/utils'
import type { ChainId } from '@/sdk'

type SwapLegRef = { address: string; symbol: string; role?: TokenLegRole }

/** ID estable para URLs y Redux: nativo → símbolo de cadena; ERC-20 → on-chain checksummed. */
export function toSwapCurrencyId(
  address: string,
  chainId: ChainId,
  role?: string,
): string {
  if (role === 'native' || isNativeLegAddress(address) || isNativeCurrencySymbol(address, chainId)) {
    return nativeCurrencyId(chainId)
  }
  const onChain = toOnChainAddress(address)
  const checksummed = isAddress(onChain)
  return typeof checksummed === 'string' ? checksummed : address
}

/** Swap en sentido compra del base: pagar quote → recibir base. */
export function swapIdsForBuyBase(
  base: SwapLegRef,
  quote: SwapLegRef,
  chainId: ChainId,
): { inputCurrencyId: string; outputCurrencyId: string } {
  return {
    inputCurrencyId: toSwapCurrencyId(quote.address, chainId, quote.role),
    outputCurrencyId: toSwapCurrencyId(base.address, chainId, base.role),
  }
}

function resolveLegMeta(legId: string, chainId: ChainId): SwapLegRef {
  const nativeId = nativeCurrencyId(chainId)
  if (legId === nativeId || isNativeCurrencySymbol(legId, chainId)) {
    return { address: NATIVE_TOKEN_ADDRESS, symbol: nativeId, role: 'native' }
  }

  const catalog = getTokenCatalog(chainId)
  const normalized = legId.toLowerCase()
  const entry = catalog.find((token) => token.catalogAddress.toLowerCase() === normalized)
  if (entry) {
    return {
      address: entry.catalogAddress,
      symbol: entry.symbol,
      role: entry.role,
    }
  }

  return {
    address: legId,
    symbol: legId,
    role: getTokenLegRole(legId, chainId) as TokenLegRole,
  }
}

/** Resuelve ids de sesión para comprar el base de un par (desde ids de pierna del picker). */
export function swapIdsForBuyBaseFromLegIds(
  legAId: string,
  legBId: string,
  chainId: ChainId,
): { inputCurrencyId: string; outputCurrencyId: string } {
  const legA = resolveLegMeta(legAId, chainId)
  const legB = resolveLegMeta(legBId, chainId)
  const [base, quote] = orderPairLegsForDisplay(legA, legB, chainId)
  return swapIdsForBuyBase(base, quote, chainId)
}

export function swapIdsFromPoolLegs(
  pool: {
    baseToken?: SwapLegRef | null
    quoteToken?: SwapLegRef | null
    token0: SwapLegRef
    token1: SwapLegRef
  },
  chainId: ChainId,
): { inputCurrencyId: string; outputCurrencyId: string } {
  if (pool.baseToken && pool.quoteToken) {
    return swapIdsForBuyBase(pool.baseToken, pool.quoteToken, chainId)
  }

  const [base, quote] = orderPairLegsForDisplay(pool.token0, pool.token1, chainId)
  return swapIdsForBuyBase(base, quote, chainId)
}
